import ChartTooltip from "@ui/ChartTooltip";
import PieChart, { ECOption, PieChartRef } from "@ui/PieChart";
import { PieSeriesOption } from "echarts/charts";
import type { TooltipComponentOption } from "echarts/components";
import { TFunction } from "i18next";
import { ForwardedRef } from "react";
import { renderToString } from "react-dom/server";
import { useTranslation } from "react-i18next";

import EllipseSvg from "@/assets/common/other_icons/ellipse.svg?react";
import { ReviewCategory } from "@/types";
import { formatCarbon, getCarbonDisplayUnit } from "@/utils/carbon";

function optionFactory(
  data: Array<ReviewCategory>,
  extraSeriesParams: PieSeriesOption,
  extraChartParams: ECOption,
  t: TFunction,
  colors?: Array<string>,
  title?: string,
  titleSubText?: string
): ECOption {
  let fontSize = "2rem";
  if (title && title.length > 8) {
    fontSize = "1.5rem";
  }

  // Get the best unit
  const dataUnit = getCarbonDisplayUnit(
    data.map((dataItem) => dataItem.carbon)
  );

  // Convert all the carbon values to the provided unit
  // and store the localized carbon string.
  const formattedData = data.map((dataItem) => {
    const { formattedCarbon, convertedCarbon } = formatCarbon(
      t,
      dataItem.carbon,
      dataUnit
    );
    return {
      ...dataItem,
      carbon: convertedCarbon,
      carbonToDisplay: formattedCarbon,
    };
  }) as Array<ReviewCategory & { carbonToDisplay: string }>;

  return {
    textStyle: {
      fontFamily: "Maven Pro",
    },
    color: colors,
    title: {
      text: title,
      show: Boolean(title),
      left: "center",
      top: "center",
      textVerticalAlign: "top",
      subtext: titleSubText,
      itemGap: 5,
      textStyle: {
        fontSize,
        fontWeight: 700,
        color: "#09044A",
      },
      subtextStyle: {
        fontSize: 14,
        fontWeight: 400,
        color: "#09044A",
      },
    },
    tooltip: {
      borderWidth: 0,
      padding: 0,
      extraCssText: "border-radius: 8px;",
      formatter: ({ name, color }: { name: string; color: string }) =>
        renderToString(
          <ChartTooltip
            seriesName={t(`reviewCategories:${name}`)}
            valueToDisplay={
              formattedData.find((category) => category.key === name)!
                .carbonToDisplay
            }
            unit={t(`common:unit.${dataUnit}`)}
            color={color}
            Icon={EllipseSvg}
            iconClassName="size-2"
          />
        ),
    } as TooltipComponentOption,
    series: [
      {
        type: "pie",
        radius: ["65%", "85%"],
        avoidLabelOverlap: false,
        label: { show: false },
        labelLine: { show: false },
        data: formattedData.map((item) => {
          return {
            value: item.carbon,
            name: item.key,
            key: item.key,
            itemStyle: {
              color: item.color,
            },
          };
        }),
        ...extraSeriesParams,
      },
    ],
    ...extraChartParams,
  };
}

function ReviewPie({
  data,
  colors,
  title,
  titleSubText,
  chartRef,
  onClick,
  extraSeriesParams = {},
  extraChartParams = {},
}: {
  data: Array<ReviewCategory>;
  chartRef?: ForwardedRef<PieChartRef>;
  onClick?: (key: string | undefined) => void;
  colors?: Array<string>;
  title?: string;
  titleSubText?: string;
  extraSeriesParams?: PieSeriesOption;
  extraChartParams?: ECOption;
}) {
  const { t } = useTranslation(["reviewCategories", "common", "formatter"]);

  const option = optionFactory(
    data,
    extraSeriesParams,
    extraChartParams,
    t,
    colors,
    title,
    titleSubText
  );

  return <PieChart option={option} onClick={onClick} ref={chartRef} />;
}

export default ReviewPie;
